import { useState } from 'react';
import { Link } from 'react-router-dom';
import { LayoutGrid, Plus, Sparkles, UserPlus } from 'lucide-react';
import { PageHeader } from '@/components/shared/page';
import { DataTable, type Column } from '@/components/shared/data-table';
import { Can } from '@/components/shared/can';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox, Select } from '@/components/ui/misc';
import { useDebounce } from '@/hooks/use-debounce';
import { usePagination } from '@/hooks/use-pagination';
import { formatMoney, timeAgo } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useBulkLeads, useLeads } from './api';
import { LEAD_SOURCE_LABELS, LEAD_STATUS_LABELS, LEAD_STATUS_VARIANT, scoreBand } from './labels';
import { LeadDialog } from './lead-dialog';
import { ConvertLeadDialog } from './convert-lead-dialog';
import type { Lead, LeadStatus } from './types';

const STATUSES = Object.keys(LEAD_STATUS_LABELS) as LeadStatus[];

export function LeadsPage() {
  const pagination = usePagination();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [sort, setSort] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Lead | null>(null);
  const [converting, setConverting] = useState<Lead | null>(null);

  const debounced = useDebounce(search);
  const bulk = useBulkLeads();

  const { data, isLoading, error, refetch } = useLeads({
    page: pagination.page,
    limit: pagination.limit,
    q: debounced || undefined,
    status: status || undefined,
    sort: sort || undefined,
  });

  const rows = data?.data ?? [];
  const allSelected = rows.length > 0 && selected.length === rows.length;

  const toggleAll = () => setSelected(allSelected ? [] : rows.map((row) => row.id));
  const toggle = (id: string) =>
    setSelected((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );

  const columns: Column<Lead>[] = [
    {
      key: 'select',
      header: '',
      headerClassName: 'w-8',
      className: 'w-8',
      cell: (row) => (
        <span onClick={(event) => event.stopPropagation()}>
          <Checkbox checked={selected.includes(row.id)} onCheckedChange={() => toggle(row.id)} />
        </span>
      ),
    },
    {
      key: 'name',
      header: 'Lead',
      sortKey: 'first_name',
      cell: (row) => (
        <div className="min-w-0">
          <div className="truncate font-medium">{row.name}</div>
          <div className="truncate text-xs text-muted-foreground">
            {row.companyName ?? row.email ?? row.phone ?? row.number}
          </div>
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      className: 'text-sm',
      cell: (row) => (
        <div className="min-w-0">
          {row.email && <div className="truncate">{row.email}</div>}
          {row.phone && <div className="truncate text-xs text-muted-foreground">{row.phone}</div>}
          {!row.email && !row.phone && <span className="text-muted-foreground">—</span>}
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      className: 'text-sm text-muted-foreground',
      cell: (row) => LEAD_SOURCE_LABELS[row.source],
    },
    {
      key: 'score',
      header: 'Score',
      sortKey: 'score',
      cell: (row) => {
        const band = scoreBand(row.score);
        return (
          <span
            className={cn('rounded-full px-2 py-0.5 text-xs font-medium', band.className)}
            title={`${row.score} / 100`}
          >
            {band.label} · {row.score}
          </span>
        );
      },
    },
    {
      key: 'budget',
      header: 'Budget',
      className: 'tabular text-sm',
      cell: (row) => formatMoney(row.budget, row.currency),
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      cell: (row) => (
        <Badge variant={LEAD_STATUS_VARIANT[row.status]}>{LEAD_STATUS_LABELS[row.status]}</Badge>
      ),
    },
    {
      key: 'owner',
      header: 'Owner',
      className: 'text-sm text-muted-foreground',
      cell: (row) => row.owner?.name ?? 'Unassigned',
    },
    {
      key: 'created',
      header: 'Added',
      sortKey: 'created_at',
      className: 'whitespace-nowrap text-sm text-muted-foreground',
      cell: (row) => timeAgo(row.createdAt),
    },
    {
      key: 'actions',
      header: '',
      cell: (row) =>
        row.status === 'CONVERTED' ? (
          <Link
            to={`/crm/deals/${row.convertedDealId ?? ''}`}
            onClick={(event) => event.stopPropagation()}
            className="text-xs text-muted-foreground hover:underline"
          >
            View deal
          </Link>
        ) : (
          <Can permission="crm.lead.update">
            <button
              onClick={(event) => {
                event.stopPropagation();
                setConverting(row);
              }}
              className="flex items-center gap-1 text-xs text-primary hover:underline"
            >
              <Sparkles className="h-3 w-3" />
              Convert
            </button>
          </Can>
        ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Leads"
        description="Unqualified enquiries. Converting one creates the company, contact and deal together."
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link to="/crm/leads/board">
                <LayoutGrid className="h-4 w-4" />
                Pipeline view
              </Link>
            </Button>
            <Can permission="crm.lead.create">
              <Button onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" />
                New lead
              </Button>
            </Can>
          </div>
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            pagination.reset();
          }}
          placeholder="Search name, company, email or phone…"
          className="h-9 w-full sm:max-w-xs"
        />
        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value);
            pagination.reset();
          }}
          className="h-9 w-auto min-w-[10rem]"
        >
          <option value="">All statuses</option>
          {STATUSES.map((value) => (
            <option key={value} value={value}>
              {LEAD_STATUS_LABELS[value]}
            </option>
          ))}
        </Select>

        {selected.length > 0 && (
          <div className="ml-auto flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-1.5">
            <span className="text-sm">{selected.length} selected</span>
            <Select
              className="h-7 w-auto text-xs"
              value=""
              onChange={(event) => {
                const value = event.target.value;
                if (!value) return;
                bulk.mutate(
                  { ids: selected, action: 'status', status: value },
                  { onSuccess: () => setSelected([]) },
                );
              }}
            >
              <option value="">Set status…</option>
              {STATUSES.filter((s) => s !== 'CONVERTED' && s !== 'UNQUALIFIED').map((value) => (
                <option key={value} value={value}>
                  {LEAD_STATUS_LABELS[value]}
                </option>
              ))}
            </Select>
            <button
              onClick={() => setSelected([])}
              className="text-xs text-muted-foreground hover:underline"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="mb-2 flex items-center gap-2 px-1">
          <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
          <span className="text-xs text-muted-foreground">Select all on this page</span>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        isLoading={isLoading}
        error={error ? 'Could not load leads.' : null}
        onRetry={() => void refetch()}
        meta={data?.meta}
        onPageChange={pagination.setPage}
        onRowClick={(row) => row.status !== 'CONVERTED' && setEditing(row)}
        sort={sort}
        onSortChange={setSort}
        emptyTitle="No leads yet"
        emptyDescription="Capture your first enquiry to get the pipeline moving."
        emptyAction={
          <Can permission="crm.lead.create">
            <Button onClick={() => setCreating(true)}>
              <UserPlus className="h-4 w-4" />
              New lead
            </Button>
          </Can>
        }
      />

      {(creating || editing) && (
        <LeadDialog
          lead={editing ?? undefined}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
      {converting && (
        <ConvertLeadDialog lead={converting} onClose={() => setConverting(null)} />
      )}
    </div>
  );
}
